import React, { useState, useEffect } from "react";
import { api, formatApiError } from "@/lib/api";
import { SERVICES } from "./Services";
import { CalendarCheck, CheckCircle2 } from "lucide-react";

const initial = {
  name: "",
  email: "",
  phone: "",
  car_model: "",
  service: "",
  date_time: "",
  message: "",
};

export default function BookingForm() {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  // Pre-select service from URL: /?service=Klíma+javítás-töltés#foglalas
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const requested = params.get("service");
    if (!requested) return;
    const match = SERVICES.find((s) => s.title === requested);
    if (match) {
      setForm((prev) => ({ ...prev, service: match.title }));
      // remove the query param from URL but keep the hash to avoid re-trigger on remount
      const cleanUrl =
        window.location.pathname + (window.location.hash || "");
      window.history.replaceState({}, "", cleanUrl);
    }
  }, []);

  const change = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.service) {
      setError("Kérjük, válasszon szolgáltatást.");
      return;
    }
    if (!form.date_time) {
      setError("Kérjük, adjon meg egy kívánt időpontot.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/bookings", form);
      setSuccess(true);
      setForm(initial);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Hiba történt.");
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <section
        id="foglalas"
        className="relative py-24 sm:py-32 border-t border-white/5"
        data-testid="booking-section"
      >
        <div className="max-w-3xl mx-auto px-6 lg:px-10 text-center">
          <CheckCircle2
            className="w-16 h-16 text-[#22C55E] mx-auto mb-6"
            strokeWidth={1.4}
          />
          <h2
            className="font-black uppercase text-white leading-tight"
            style={{
              fontFamily: "Barlow Condensed",
              fontSize: "clamp(2rem, 4vw, 3rem)",
            }}
            data-testid="booking-success-heading"
          >
            Köszönjük a foglalását!
          </h2>
          <p className="mt-4 text-zinc-400 leading-relaxed">
            Kollégánk hamarosan felveszi Önnel a kapcsolatot a megadott
            elérhetőségeken az időpont megerősítésére.
          </p>
          <button
            onClick={() => setSuccess(false)}
            className="btn-outline mt-8"
            data-testid="booking-new-btn"
          >
            Új foglalás
          </button>
        </div>
      </section>
    );
  }

  return (
    <section
      id="foglalas"
      className="relative py-24 sm:py-32 border-t border-white/5"
      data-testid="booking-section"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <span className="section-eyebrow mb-4">03 / Időpontfoglalás</span>
          <h2
            className="mt-5 font-black uppercase text-white leading-[0.95]"
            style={{
              fontFamily: "Barlow Condensed",
              fontSize: "clamp(2.25rem, 5vw, 4rem)",
            }}
          >
            Foglaljon
            <br />
            <span className="text-[#0082D2]">időpontot</span> online.
          </h2>
          <p className="mt-6 text-zinc-400 leading-relaxed max-w-md">
            Töltse ki az alábbi űrlapot, és visszahívjuk a megerősített
            időponttal. A foglalás ingyenes és kötelezettség nélküli.
          </p>

          <div className="mt-10 hidden lg:block">
            <div className="border border-white/10 p-6">
              <CalendarCheck
                className="w-7 h-7 text-[#0082D2] mb-3"
                strokeWidth={1.5}
              />
              <div
                className="text-sm font-bold uppercase tracking-wider text-white"
                style={{ fontFamily: "Barlow Condensed" }}
              >
                Gyors visszahívás
              </div>
              <div className="text-sm text-zinc-400 mt-1">
                Munkanapokon 24 órán belül megerősítjük az időpontot.
              </div>
            </div>
          </div>
        </div>

        <form
          onSubmit={submit}
          className="lg:col-span-7 bg-[#101010] border border-white/5 p-8 sm:p-10 grid sm:grid-cols-2 gap-5"
          data-testid="booking-form"
        >
          <Field label="Teljes név" required>
            <input
              required
              type="text"
              className="input-field"
              value={form.name}
              onChange={change("name")}
              data-testid="booking-name"
            />
          </Field>
          <Field label="Telefonszám" required>
            <input
              required
              type="tel"
              className="input-field"
              value={form.phone}
              onChange={change("phone")}
              placeholder="+36 30 123 4567"
              data-testid="booking-phone"
            />
          </Field>
          <Field label="E-mail cím" required>
            <input
              required
              type="email"
              className="input-field"
              value={form.email}
              onChange={change("email")}
              data-testid="booking-email"
            />
          </Field>
          <Field label="BMW típus / évjárat" required>
            <input
              required
              type="text"
              className="input-field"
              value={form.car_model}
              onChange={change("car_model")}
              placeholder="pl. F30 320d 2015"
              data-testid="booking-car-model"
            />
          </Field>
          <Field label="Válasszon szolgáltatást" required>
            <select
              required
              className="input-field"
              value={form.service}
              onChange={change("service")}
              data-testid="booking-service"
            >
              <option value="">— Válasszon —</option>
              {SERVICES.map((s) => (
                <option key={s.id} value={s.title}>
                  {s.title}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Kívánt időpont" required>
            <input
              required
              type="datetime-local"
              className="input-field"
              value={form.date_time}
              onChange={change("date_time")}
              data-testid="booking-datetime"
            />
          </Field>
          <div className="sm:col-span-2">
            <Field label="Egyéb megjegyzés, hibaleírás">
              <textarea
                className="input-field"
                rows={4}
                value={form.message}
                onChange={change("message")}
                placeholder="Pl. a klíma nem hűt megfelelően..."
                data-testid="booking-message"
              />
            </Field>
          </div>

          {error && (
            <div
              className="sm:col-span-2 text-sm text-red-400 border border-red-500/30 bg-red-500/5 px-4 py-3"
              data-testid="booking-error"
            >
              {error}
            </div>
          )}

          <div className="sm:col-span-2 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pt-2">
            <p className="text-xs text-zinc-500">
              Az űrlap elküldésével hozzájárul adatainak feldolgozásához az{" "}
              <a
                href="/adatkezelesi-tajekoztato"
                target="_blank"
                rel="noopener"
                className="text-[#0082D2] hover:underline"
              >
                adatkezelési tájékoztató
              </a>{" "}
              szerint.
            </p>
            <button
              type="submit"
              disabled={loading}
              className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="booking-submit-btn"
            >
              {loading ? "Küldés..." : "Foglalás elküldése"}
            </button>
          </div>
        </form>
      </div>
    </section>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="label-text">
        {label} {required && <span className="text-[#0082D2]">*</span>}
      </span>
      {children}
    </label>
  );
}
